import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { KeyRound, ShieldCheck, Eye, EyeOff } from 'lucide-react';

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get('token') || '';
  const [form, setForm] = useState({ new_password: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  useSEO({
    title: 'Restablecer contraseña | AutoMarket Colombia',
    description: 'Establece una nueva contraseña para tu cuenta.',
    canonical: '/restablecer-contrasena',
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Enlace inválido: falta el token');
      return;
    }
    if (form.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: form.new_password });
      toast.success('Contraseña actualizada. Ya puedes iniciar sesión.');
      nav('/login');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo restablecer la contraseña');
    } finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-md px-4 py-12 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Cuenta</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1 flex items-center gap-2">
          <KeyRound className="h-7 w-7 text-slate-600" /> Nueva contraseña
        </h1>
        {!token ? (
          <div className="mt-6 border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="reset-no-token">
            El enlace no contiene un token válido. Vuelve a{' '}
            <Link to="/olvide-contrasena" className="font-bold underline">solicitar el enlace</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Nueva contraseña</label>
              <div className="mt-1 relative">
                <Input
                  data-testid="reset-new-password-input"
                  type={show ? 'text' : 'password'}
                  required
                  value={form.new_password}
                  onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                  placeholder="Mín. 6 caracteres"
                  className="rounded-none pr-10"
                />
                <button
                  type="button"
                  data-testid="reset-toggle-visibility"
                  onClick={() => setShow((v) => !v)}
                  aria-label={show ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-900"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Confirmar contraseña</label>
              <Input
                data-testid="reset-confirm-password-input"
                type={show ? 'text' : 'password'}
                required
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="rounded-none mt-1"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="reset-submit-btn"
              className="w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {loading ? 'Actualizando...' : 'Actualizar contraseña'}
            </Button>
            <Link
              to="/login"
              className="block text-center text-sm font-semibold text-blue-700 hover:underline"
            >
              ← Volver al inicio de sesión
            </Link>
          </form>
        )}
      </div>
    </Layout>
  );
}
